import { ArrayTwoOrMore } from "@eave/shared/types/generic";
import { formatCurrency } from "@eave/shared/utils/currency";
import React from "react";
import LabeledValue from "./LabeledValue";

const styles = require("./styles.module.scss");

export type SumSectionHeadingProps = {
  value: number; // e.g. "123456.99"
  description?: string; //  e.g. "Total Reserves Amount"
};

// The value/description from SumSectionHeadingProps will be for the sum of the addends
type Props = SumSectionHeadingProps & {
  sectionLabel: string;
  addends?: ArrayTwoOrMore<SumSectionHeadingProps>; // in "1 + 1 = 2", the 1's are the "addends"
  children?: React.ReactNode;
};

const SumSectionHeading: React.FC<Props> = ({
  sectionLabel,

  value: sumTotal, // value of the sum
  description: sumDescription, // description of the sum

  addends,
}: Props) => {
  // If the addends don't add up to the sum, log a warning
  const addendsDoNotAddUpToGivenSum =
    addends?.length > 0 && addends.reduce((acc, addend) => acc + addend.value, 0) !== sumTotal;

  if (addendsDoNotAddUpToGivenSum) {
    console.warn(
      `The addends (${addends
        .map(a => a.value)
        .join(", ")}) do not add up to the given sum (${sumTotal}).`,
    );
  }

  return (
    <div className={styles.sectionHeadingContainer}>
      {sectionLabel && <h2 className={styles.sectionLabel}>{sectionLabel}</h2>}
      <div className={styles.sumSectionHeadingEquation}>
        <LabeledValue heading={formatCurrency(sumTotal, 2, 2)} subheading={sumDescription} />

        {/* Require at least two addends, even for empty values, so equations are always very explicit and clear */}
        {addends?.length >= 2 &&
          // Some looping, with index-checks for the =/+ signs
          addends.map((addend, i) => (
            <>
              {i === 0 && <LabeledValue heading="=" />}
              <div style={addend?.value === 0 ? { opacity: "0.4" } : {}}>
                <LabeledValue
                  heading={formatCurrency(addend.value, 2, 2)}
                  subheading={addend.description}
                />
              </div>
              {/* Always express as an addition equation. Subtraction can be shown as the sum of a negative number */}
              {i <= addends.length - 2 && <LabeledValue heading={"+"} />}
            </>
          ))}
      </div>
    </div>
  );
};

export default SumSectionHeading;
